import { Link } from 'react-router-dom';
import { ArrowInline } from "@/lib/Icon";
import { format, parseISO } from 'date-fns';

import { usePharmacyWarnings } from '@/lib/api/adminHooks';
import type { PharmacyWarningRow } from '@/types/admin';

const PREVIEW_LIMIT = 3;

/**
 * Compact summary card for the Clinical Ops dashboard. Shows unresolved
 * + resolved counts plus a few preview rows of the most recent
 * unresolved warnings. "View all →" links to the dedicated warnings
 * screen for the full list + filters + action UI.
 */
export function PharmacyWarningsSection() {
  const { data, isLoading, isError, error } = usePharmacyWarnings('unresolved');

  return (
    <section className="flex flex-col gap-12">
      <div className="flex items-baseline justify-between">
        <h2 className="type-heading-lg">Pharmacy warnings</h2>
        <Link
          to="/admin/pharmacy/warnings"
          className="type-body-sm text-accent hover:underline"
        >
          View all <ArrowInline />
        </Link>
      </div>

      {isLoading ? (
        <p className="type-body-sm text-ink-muted">Loading…</p>
      ) : isError ? (
        <p className="type-body-sm text-status-danger">
          Failed to load: {String((error as Error)?.message || error)}
        </p>
      ) : (
        <SummaryBody data={data!} />
      )}
    </section>
  );
}

function SummaryBody({
  data,
}: {
  data: { warnings: PharmacyWarningRow[]; unresolved_count: number; resolved_count: number };
}) {
  const unresolved = data.unresolved_count;
  const resolved = data.resolved_count;
  const previews = data.warnings.slice(0, PREVIEW_LIMIT);

  return (
    <div className="flex flex-col gap-12">
      <div className="grid grid-cols-2 gap-12">
        <KpiTile
          label="Unresolved"
          value={unresolved}
          tone={unresolved > 0 ? 'danger' : 'muted'}
        />
        <KpiTile label="Resolved" value={resolved} tone="muted" />
      </div>

      {previews.length === 0 ? (
        <p className="type-body-sm text-ink-muted">
          No unresolved pharmacy warnings — all clear.
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          {previews.map((w) => (
            <PreviewRow
              key={`${w.order_id}:${w.reason_code}:${w.received_at}`}
              w={w}
            />
          ))}
          {unresolved > previews.length ? (
            <Link
              to="/admin/pharmacy/warnings"
              className="type-body-sm text-accent hover:underline self-end pt-2"
            >
              + {unresolved - previews.length} more <ArrowInline />
            </Link>
          ) : null}
        </div>
      )}
    </div>
  );
}

function KpiTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'danger' | 'muted';
}) {
  const valueClass =
    tone === 'danger' && value > 0
      ? 'type-heading-lg text-status-danger'
      : 'type-heading-lg text-ink';
  return (
    <div className=" border border-default bg-surface-card p-15 flex flex-col gap-2">
      <p className="type-label-sm text-ink-muted">{label}</p>
      <p className={valueClass}>{value}</p>
    </div>
  );
}

function PreviewRow({ w }: { w: PharmacyWarningRow }) {
  const received = w.received_at
    ? format(parseISO(w.received_at), 'MMM d')
    : '—';
  const note = (w.notes || '').trim();
  const noteShort = note.length > 90 ? `${note.slice(0, 90)}…` : note;
  return (
    <Link
      to="/admin/pharmacy/warnings"
      className="rounded-sm border border-subtle bg-surface-soft/40 hover:bg-surface-soft p-10 flex flex-col gap-2"
    >
      <div className="flex items-baseline justify-between gap-12">
        <p className="type-body-sm text-ink">
          <span className="type-label-sm text-ink-emphasis bg-surface-card border border-default rounded-sm px-4 py-1 mr-6">
            {w.reason_code ?? '?'}
          </span>
          {w.reason_description || 'no description'}
        </p>
        <p className="type-body-xs text-ink-muted whitespace-nowrap">
          {w.order_number || '—'} · {received}
        </p>
      </div>
      {noteShort ? (
        <p className="type-body-xs text-ink-muted">"{noteShort}"</p>
      ) : null}
    </Link>
  );
}
